import { Component, Input, OnInit, OnChanges, SimpleChanges, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';

import { RapiderListGridComponent } from '@rapider/list-grid';
import { RapiderJsonViewerComponent } from '@rapider/components/json-viewer';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';

import { ProjectModelControllerService } from '@rapider/sdk';
import { ProjectDataService, selectProjectModelsList } from '@rapider/project-data';

import {
  DATA_INSIGHT_CONFIG,
  extractUiEntityConfig,
  buildFetchRecordsWithRelationsPath,
  generateTimelineGridConfig,
  getRelationDisplayType,
  buildUpdateDiffClassMap,
  getGridConfigForModel,
  getTargetModelTitle
} from '../database.config';
import { DataCardComponent } from '../data-card/data-card.component';

/**
 * @class ProjectModelDataViewerComponent
 * @description A generic, reusable component that fetches and visualizes project model records alongside their relation data.
 * Leverages the `uiEntityConfig` metadata automatically to apply proper data grid columns and API filters.
 */
@Component({
  selector: 'rapider-project-model-data-viewer',
  standalone: true,
  imports: [
    CommonModule,
    RapiderListGridComponent,
    RapiderJsonViewerComponent,
    NzTagModule,
    NzDividerModule,
    DataCardComponent,
    NzPaginationModule
  ],
  templateUrl: './project-model-data-viewer.component.html'
})
export class ProjectModelDataViewerComponent implements OnInit, OnChanges {
  private readonly store = inject(Store);
  private readonly projectDataService = inject(ProjectDataService);
  private readonly projectModelController = inject(ProjectModelControllerService);


  @Input({ required: true }) projectModelName!: string;
  @Input() recordIds?: string[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() payloadData: any = null;
  @Input() operationType: string = 'READ';

  public config = DATA_INSIGHT_CONFIG;
  public getRelationDisplayType = getRelationDisplayType;
  public generateTimelineGridConfig = generateTimelineGridConfig;
  public getGridConfigForModel = getGridConfigForModel;
  public getTargetModelTitle = getTargetModelTitle;

  public isLoading = signal<boolean>(false);
  public isRelationsLoading = signal<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public data = signal<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public selectedRecord = signal<any | null>(null);
  public hoveredRecord = signal<any | null>(null);

  // Dynamically returns the selected record, or falls back to the hovered preview if nothing is explicitly selected
  public activeDisplayRecord = computed(() => this.selectedRecord() || this.hoveredRecord());

  public cachedModels = computed(() => this.store.selectSignal(selectProjectModelsList)() || []);

  // Pagination State Signals
  public currentPage = signal<number>(1);
  public pageSize = computed<number>(() => {
    // Dynamically retrieve pagination limit from the listGridConfig, defaulting to 40
    const configSize = this.mainGridConfig()?.pagination?.pageSize;
    return configSize && typeof configSize === 'number' ? configSize : 40;
  });
  public totalItems = signal<number>(0);

  // Local text config to strictly avoid hardcoded text
  public localTexts = {
    specificRecordsInfo: $localize`:@@dataViewer.specificRecordsInfo:Showing results for specific records`,
    noData: $localize`:@@dataViewer.noDataSpreadsheet:No data available. Please enter data directly into the spreadsheet to create new records.`
  };

  /**
   * PURE FUNCTION: Constructs the API path to fetch paginated "All Data".
   * @param {string} modelName - The active datatable name.
   * @param {number} skip - Number of records to bypass.
   * @param {number} limit - The page size limit.
   * @returns {string} Restful GET path with query variables.
   */
  private buildFindAllPath(modelName: string, skip: number, limit: number): string {
    return `${this.model().basePath}?skip=${skip}&limit=${limit}`;
  }

  public model = computed(() => {
    const models = this.cachedModels();
    if (!models) return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return models.find((m: any) =>
      (m.name && m.name.toLowerCase() === this.projectModelName.toLowerCase()) ||
      (m.dataTableName && m.dataTableName.toLowerCase() === this.projectModelName.toLowerCase())
    ) || null;
  });

  public uiConfig = computed(() => extractUiEntityConfig(this.model()));

  public mainGridConfig = computed(() => {
    // We no longer inject the explicit action button, relying on native row clicks and hovers instead
    return this.uiConfig()?.config?.listGridConfig || generateTimelineGridConfig(this.data());
  });

  public diffClassMap = computed(() => {
    if (this.operationType === 'UPDATE' && this.payloadData) {
      return buildUpdateDiffClassMap(this.payloadData);
    }
    return {};
  });

  ngOnInit() {
    this.fetchRelationsAndData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes['projectModelName'] && !changes['projectModelName'].isFirstChange()) ||
      (changes['recordIds'] && !changes['recordIds'].isFirstChange())) {
      this.currentPage.set(1); // Reset to first page on context change
      this.fetchRelationsAndData();
    }
  }

  private fetchRelationsAndData(): void {
    const currentModel = this.model();

    if (!currentModel || !currentModel.id) {
      // If model doesn't exist in store, we attempt to fetch it dynamically using the smart cache wrapper.
      this.isLoading.set(true);

      this.projectDataService.getProjectModelWithoutRelations(this.projectModelName).subscribe({
        next: (res: any[]) => {
          if (res && res.length > 0) {
            this.fetchFullModelAndRelations(res[0].id);
          } else {
            this.fetchData([]); // Proceed without relations if not found
          }
        },
        error: () => this.fetchData([])
      });
      return;
    }

    if (currentModel.relations && Array.isArray(currentModel.relations)) {
      // Relations and full model details are already cached in the NgRx store
      this.fetchData(currentModel.relations);
    } else {
      // Relations are missing, fetch the full model and its relations via the centralized cache wrapper
      this.fetchFullModelAndRelations(currentModel.id);
    }
  }

  /**
   * Fetches the comprehensive model details via getProjectModelCompleteDetail.
   * Caches the enriched model back into the NgRx store automatically.
   * @param {string} modelId - The ID of the project model.
   */
  private fetchFullModelAndRelations(modelId: string): void {
    this.isRelationsLoading.set(true);

    this.projectDataService.getProjectModelCompleteDetail(modelId).subscribe({
      next: (completeModel) => {
        this.isRelationsLoading.set(false);

        // Proceed to fetch the main record data now that we know what to include
        this.fetchData(completeModel.relations || []);
      },
      error: (err) => {
        console.error('Failed to fetch model and relations:', err);
        this.isRelationsLoading.set(false);
        this.fetchData([]); // Proceed without relations if the fetch fails
      }
    });
  }

  private fetchData(relations: any[]): void {
    if (!this.projectModelName) {
      this.data.set([]);
      this.selectedRecord.set(null);
      this.isLoading.set(false);
      return;
    }

    this.isLoading.set(true);
    const isSpecificRecordsMode = this.recordIds && this.recordIds.length > 0;

    if (isSpecificRecordsMode) {
      // MODE 1: Specific Records (Unpaginated Sequence)
      const path = buildFetchRecordsWithRelationsPath(this.projectModelName, this.recordIds!, relations);

      this.projectDataService.executeProjectApiCall('GET', path, null).subscribe({
        next: (response) => {
          const arr = Array.isArray(response) ? response : (response ? [response] : []);

          // SORT to strictly match the incoming recordIds sequence (Newest -> Oldest)
          const sortedArr: any[] = [];
          const idMap = new Map();
          arr.forEach(item => {
            const id = item.id || item._id;
            if (id) idMap.set(id.toString(), item);
          });

          // Push matching records in the exact priority order
          this.recordIds!.forEach(id => {
            const key = id.toString();
            if (idMap.has(key)) {
              sortedArr.push(idMap.get(key));
              idMap.delete(key);
            }
          });

          // Append any stragglers safely (fallback)
          idMap.forEach(item => sortedArr.push(item));

          this.data.set(sortedArr);
          this.totalItems.set(sortedArr.length);

          // Automatically select the very first record (the newest/active one)
          if (sortedArr.length > 0) {
            this.selectedRecord.set(sortedArr[0]);
          } else {
            this.selectedRecord.set(null);
          }

          this.isLoading.set(false);
        },
        error: () => {
          this.data.set([]);
          this.selectedRecord.set(null);
          this.isLoading.set(false);
        }
      });
    } else {
      // MODE 2: "All Data" with Pagination via Find API
      const limit = this.pageSize();
      const skip = (this.currentPage() - 1) * limit;
      const path = this.buildFindAllPath(this.projectModelName, skip, limit);

      this.projectDataService.executeProjectApiCall('GET', path, null).subscribe({
        next: (response: any) => {
          // Robust mapping to catch standard arrays or structured paginated responses { items, total }
          let records: any[] = [];
          let totalCount = 0;

          if (Array.isArray(response)) {
            records = response;
            totalCount = response.length; // Fallback if backend does not provide total in array format
          } else if (response && response.items) {
            records = response.items;
            totalCount = response.total || response.count || records.length;
          } else if (response) {
            records = [response];
            totalCount = 1;
          }

          this.data.set(records);
          this.totalItems.set(totalCount);

          if (records.length > 0) {
            this.selectedRecord.set(records[0]);
          } else {
            this.selectedRecord.set(null);
          }

          this.isLoading.set(false);
        },
        error: () => {
          this.data.set([]);
          this.selectedRecord.set(null);
          this.isLoading.set(false);
        }
      });
    }
  }

  /**
   * Action triggered when the pagination controls are interacted with.
   * @param {number} page - The selected page number.
   */
  public onPageChange(page: number): void {
    this.currentPage.set(page);
    this.fetchRelationsAndData();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onGridAction(event: any): void {
    // Safely extract the action name from the event payload
    const actionName = event?.action?.name || event?.name;

    if (actionName === 'select-item') {
      // Map the payload data into the selectedRecord signal to trigger the master-detail UI
      const recordData = event?.data || event?.item || event;
      this.selectedRecord.set(recordData);
    }
  }

  /**
   * Handles row selection on a single click. Toggles off if the same row is clicked again.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onRowClick(event: any): void {
    console.log('Row clicked:', event);
    const recordData = event?.data || event?.item || event;
    if (this.selectedRecord() === recordData) {
      this.selectedRecord.set(null); // Deselect if clicked again
    } else {
      this.selectedRecord.set(recordData);
    }
  }

  /**
   * Handles row hover to temporarily preview relations if no record is currently hard-selected.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onRowMouseEnter(event: any): void {
    const recordData = event?.data || event?.item || event;
    this.hoveredRecord.set(recordData);
  }

  /**
   * Clears the hover preview.
   */
  public onRowMouseLeave(): void {
    this.hoveredRecord.set(null);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public normalizeDataAsArray(data: any): any[] {
    if (!data) return [];
    return Array.isArray(data) ? data : [data];
  }
}